import { isAxiosError } from 'axios'

export class ApiErrorValue {
  readonly value: string
  readonly values: string[]

  constructor(input: string | string[]) {
    if (Array.isArray(input)) {
      this.values = input.filter(Boolean)
      this.value = this.values[0] ?? ''
    } else {
      this.value = input
      this.values = [input]
    }
  }
}

export class ApiErrors {
  private readonly _data: Record<string, ApiErrorValue>

  constructor(data: Record<string, ApiErrorValue> = {}) {
    this._data = data
  }

  getError(key: string) {
    return this._data[key]?.value ?? ''
  }

  getErrors(key: string) {
    return this._data[key]?.values ?? []
  }

  get(key: string) {
    return this._data[key]
  }

  entries() {
    return Object.entries(this._data)
  }

  values() {
    return Object.values(this._data)
  }

  keys() {
    return Object.keys(this._data)
  }

  get size() {
    return Object.keys(this._data).length
  }

  get first() {
    return Object.values(this._data)[0]?.value ?? ''
  }

  toRecord() {
    const result: Record<string, string> = {}
    for (const [key, val] of Object.entries(this._data)) {
      result[key] = val.value
    }
    return result
  }
}

export type INormalizedApiError = ApiErrors

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const extractValue = (value: unknown): string | string[] => {
  if (typeof value === 'string') return value
  if (typeof value === 'number') return String(value)
  if (Array.isArray(value)) {
    const strings: string[] = []
    for (const key of value) {
      if (typeof key === 'string' && key) strings.push(key)
    }
    return strings.length ? strings : ''
  }
  return ''
}

const toErrorValue = (raw: unknown): ApiErrorValue | null => {
  const extracted = extractValue(raw)
  return extracted.length ? new ApiErrorValue(extracted) : null
}

const parseErrorsKey = (value: unknown): Record<string, ApiErrorValue> => {
  const result: Record<string, ApiErrorValue> = {}
  if (!isRecord(value)) return result
  for (const [key, val] of Object.entries(value)) {
    const errorValue = toErrorValue(val)
    if (errorValue) result[key] = errorValue
  }
  return result
}

const parseErrorKey = (value: unknown): Record<string, ApiErrorValue> | null => {
  if (!isRecord(value)) return null

  if (isRecord(value['body'])) {
    return parseErrorsKey(value['body'])
  }

  if (Array.isArray(value['details'])) {
    const result: Record<string, ApiErrorValue> = {}
    for (const detail of value['details']) {
      if (!isRecord(detail)) continue
      const field = detail['field']
      const error = detail['error']
      if (typeof field === 'string' && field && typeof error === 'string' && error) {
        result[field] = new ApiErrorValue(error)
      }
    }
    return result
  }

  const fields = parseErrorsKey(value)
  if (Object.keys(fields).length) return fields

  return null
}

const parseDetailKey = (value: unknown): Record<string, ApiErrorValue> => {
  const result: Record<string, ApiErrorValue> = {}

  if (typeof value === 'string' && value) {
    result['detail'] = new ApiErrorValue(value)
    return result
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      if (!isRecord(item)) continue
      const msg = item['msg']
      const loc = item['loc']
      if (typeof msg !== 'string' || !msg || !Array.isArray(loc)) continue
      const fieldKey = [...loc].reverse().find((key): key is string => typeof key === 'string')
      if (fieldKey) result[fieldKey] = new ApiErrorValue(msg)
    }
  }

  return result
}

const flattenData = (data: Record<string, unknown>): Record<string, ApiErrorValue> => {
  const result: Record<string, ApiErrorValue> = {}

  for (const [key, value] of Object.entries(data)) {
    if (key === 'errors') {
      Object.assign(result, parseErrorsKey(value))
      continue
    }

    if (key === 'error') {
      const parsed = parseErrorKey(value)
      if (parsed) {
        Object.assign(result, parsed)
        continue
      }
    }

    if (key === 'detail') {
      Object.assign(result, parseDetailKey(value))
      continue
    }

    const errorValue = toErrorValue(value)
    if (errorValue) result[key] = errorValue
  }

  return result
}

const createDefault = (message: string) => new ApiErrors({ default: new ApiErrorValue(message) })
/**
 * Normalizes API errors (AxiosError with response) into a flat errors map.
 * Network errors, cancelled requests and non-Axios exceptions are not processed —
 * they pass through with error set but no errors map.
 */
export const normalizeApiError = (e: unknown): ApiErrors => {
  if (!isAxiosError(e) || !e.response) return new ApiErrors()

  const { data } = e.response

  if (!isRecord(data)) return createDefault(e.message)

  const errors = flattenData(data)

  return Object.keys(errors).length ? new ApiErrors(errors) : createDefault(e.message)
}
